import {
  FormDrawer,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useCreateWorkflow } from '../hooks/use-workflows';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only'),
  triggerMode: z.enum(['MANUAL', 'SCHEDULED', 'WEBHOOK', 'EVENT']),
});
type FormValues = z.infer<typeof schema>;

interface WorkflowCreateDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULT_VALUES: FormValues = { name: '', slug: '', triggerMode: 'MANUAL' };

export function WorkflowCreateDrawer({ open, onOpenChange }: WorkflowCreateDrawerProps) {
  const createMutation = useCreateWorkflow();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: form.reset is stable (react-hook-form)
  useEffect(() => {
    if (open) form.reset(DEFAULT_VALUES);
  }, [open]);

  function onSubmit(data: FormValues) {
    createMutation.mutate(
      { name: data.name, slug: data.slug, triggerMode: data.triggerMode },
      {
        onSuccess: () => {
          onOpenChange(false);
          form.reset();
        },
      },
    );
  }

  return (
    <FormDrawer
      open={open}
      onOpenChange={(o) => {
        if (!o) form.reset();
        onOpenChange(o);
      }}
      title="New Workflow"
      onSubmit={form.handleSubmit(onSubmit)}
      isPending={createMutation.isPending}
      submitDisabled={!form.watch('name')?.trim() || !form.watch('slug')?.trim()}
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="workflow-name">
            Name *
          </label>
          <Input id="workflow-name" placeholder="Workflow name" {...form.register('name')} />
          {form.formState.errors.name && (
            <p className="mt-1 text-xs text-destructive">{form.formState.errors.name.message}</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="workflow-slug">
            Slug *
          </label>
          <Input id="workflow-slug" placeholder="workflow-slug" {...form.register('slug')} />
          {form.formState.errors.slug && (
            <p className="mt-1 text-xs text-destructive">{form.formState.errors.slug.message}</p>
          )}
        </div>
        <div>
          <p className="mb-1 block text-sm font-medium">Trigger Mode</p>
          <Controller
            control={form.control}
            name="triggerMode"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trigger mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MANUAL">MANUAL</SelectItem>
                  <SelectItem value="SCHEDULED">SCHEDULED</SelectItem>
                  <SelectItem value="WEBHOOK">WEBHOOK</SelectItem>
                  <SelectItem value="EVENT">EVENT</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>
    </FormDrawer>
  );
}
